/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AiProfilesPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    AI Profiles management page with comprehensive CRUD operations.
----------------------------------------------------------------*/

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useDataGrid, type DataGridFetchResult } from '../../hooks/useDataGrid';
import { ModulePageContainer } from '../../components/layouts';
import { deleteAiProfileById, navigateToAiProfileDuplicate } from './aiProfilesActions';
import { testAiProfileConnection } from './api/aiProfilesApi';
import { useAiProfilesList } from './hooks/useAiProfilesList';
import { useAiProfilesColumns } from './hooks/useAiProfilesColumns';
import { AiProfilesActions } from './components/AiProfilesActions';
import type { AiProfile } from './api/aiProfilesApi';

export default function AiProfilesPage() {
  const { t, refreshKey, i18n } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<AiProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);

  const translateRef = useRef(t);
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);

  useEffect(() => {
    translateRef.current = t;
  }, [t, refreshKey, i18n.language]);

  useEffect(() => {
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;
  }, [showSuccess, showError]);

  // Hooks
  const { fetchProfiles } = useAiProfilesList();
  const { availableColumns, getColumnRenderer } = useAiProfilesColumns();

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchProfiles as (params: { page: number; limit: number; sortBy: string; sortOrder: "desc" | "asc"; search?: string; }) => Promise<DataGridFetchResult>,
    initialColumns: availableColumns,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    initialLimit: 10,
    moduleName: 'aiProfiles'
  });

  const handleCreateProfile = useCallback(() => {
    navigate('/admin/ai-integration/profiles/new');
  }, [navigate]);

  const handleEditProfile = useCallback(
    (profile: AiProfile) => {
      navigate(`/admin/ai-integration/profiles/${profile.uuid ?? profile.id}`);
    },
    [navigate]
  );

  const handleDuplicateProfile = useCallback(
    (profile: AiProfile) => {
      navigateToAiProfileDuplicate(navigate, profile.uuid ?? profile.id);
    },
    [navigate]
  );

  const handleDeleteProfile = useCallback((profile: AiProfile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!profileToDelete) return;

    try {
      setIsDeleting(true);
      await deleteAiProfileById(profileToDelete.id);
      setShowDeleteModal(false);
      setProfileToDelete(null);
      showSuccessRef.current?.(translateRef.current('aiProfiles.messages.deleteSuccess'));
      refresh();
    } catch (error) {
      showErrorRef.current?.(error?.message || translateRef.current('aiProfiles.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [profileToDelete, refresh]);

  const handleTestConnection = useCallback(
    async (profile: AiProfile) => {
      setTestingConnection(profile.id);
      try {
        const result = await testAiProfileConnection(profile.id);
        if (result?.success) {
          showSuccessRef.current?.(translateRef.current('aiProfiles.messages.testSuccess'));
          refresh();
        } else {
          const fallback = result?.message ?? translateRef.current('aiProfiles.messages.testError');
          showErrorRef.current?.(fallback);
        }
      } catch (error) {
        const message =
          error instanceof Error && error.message && error.message !== 'TEST_FAILED'
            ? error.message
            : translateRef.current('aiProfiles.messages.testError');
        showErrorRef.current?.(message);
      } finally {
        setTestingConnection(null);
      }
    },
    [refresh]
  );

  const renderActions = useCallback(
    (profile: AiProfile) => (
      <AiProfilesActions
        profile={profile}
        testingConnection={testingConnection}
        onEdit={handleEditProfile}
        onDelete={handleDeleteProfile}
        onDuplicate={handleDuplicateProfile}
        onTestConnection={handleTestConnection}
      />
    ),
    [handleDeleteProfile, handleDuplicateProfile, handleEditProfile, handleTestConnection, testingConnection]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns.map(column => ({
            ...column,
            render: getColumnRenderer(column.key) || column.render
          }))}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreateProfile}
          onEdit={handleEditProfile}
          onDuplicate={handleDuplicateProfile}
          onDelete={handleDeleteProfile}
          title={t('aiProfiles.title')}
          titleIcon={<Bot className="h-5 w-5 text-primary" />}
          addButtonText={t('aiProfiles.actions.create')}
          searchPlaceholder={t('common.searchPlaceholder')}
          emptyMessage={t('common.noItemsFound')}
          deleteTitle={t('aiProfiles.actions.delete')}
          deleteMessage={item =>
            t('aiProfiles.messages.deleteConfirm', {
              name: item?.profile_name || item?.provider_name || item?.provider_type || ''
            })
          }
          renderActions={renderActions}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('aiProfiles.actions.delete')}
        message={
          profileToDelete
            ? t('aiProfiles.messages.deleteConfirm', {
                name: profileToDelete.profile_name || profileToDelete.provider_name || profileToDelete.provider_type || ''
              })
            : ''
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />
    </ModulePageContainer>
  );
}
